import { ApolloProvider } from '@apollo/react-hooks';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import smoothscroll from 'smoothscroll-polyfill';

import { showErrorBar } from 'actions/errorBar';
import {
  toggleGoogleInviteModal,
  toggleGoogleOAuthError,
} from 'actions/google';
import {
  updateFromPublicSharePage,
  updateTheaterMode,
} from 'actions/videoPlayer';
import { ERROR } from 'constants/error-severities';
import { SEND_HLS_PLAYBACK_STATS } from 'constants/featureFlag';
import { NOT_FOUND } from 'constants/routes';
import { VIDEO } from 'constants/ssr';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import { initVideoCreator } from 'creators/shareVideo';
import { getUserCreator } from 'creators/user';
import sentryActionState from 'middleware/sentry';
import { parseCookie } from 'utilities/cookie';
import { injectCustomBrandColors } from 'utilities/custom-branding';
import { getGraphQLClient } from 'utilities/graphql';
import * as logger from 'utilities/logger';
import * as loggerx from 'utilities/loggerx';
import { processPushEventData } from 'utilities/notifications';
import * as rum from 'utilities/rum';
import * as storeUtil from 'utilities/store';
import { getParam } from 'utilities/url';

import reducers from 'reducers';

import { UrlQueryParamContextWrapper } from './contexts/UrlQueryParamContext';
import { KEY_THEATER_MODE } from '../../shared/constants/cookie';
import { GOOGLE_APP } from '../../shared/constants/oAuthKeys';

import ModalContainer from 'components/modals/ModalContainer';
import ShareVideo from 'components/share-video';
import * as wru from 'components/web-recording-utility';

// 3rd-party
import 'css/medium.css';

// dd real user metrics
rum.init();

// we do this immediately in case someone tries to copy the link with the
// query param in it right after a recording
const focusTitleParam = getParam('focus_title') === '1';

const mutedParam = getParam('muted') === '1';

const fromFirstVideoViewEmail =
  getParam('source_email') === 'loom-video-first-view-updated';

const fromReactionNotification = getParam('is_reaction_notification') === '1';

const fromRecorderParam = getParam('from_recorder') === '1';

const recordingLimitParam = getParam('recording_limit') === '1';

const timeParam = getParam('t');

const oauthError = getParam('oauth_error');

const provider = getParam('provider');

const data = getParam('data') ? JSON.parse(atob(getParam('data'))) : {};

const isGoogleApp = provider === GOOGLE_APP;

// init custom logger asap
logger.init();

function init() {
  const hasParamToClear = [
    focusTitleParam,
    mutedParam,
    fromFirstVideoViewEmail,
    fromReactionNotification,
    fromRecorderParam,
    recordingLimitParam,
    oauthError,
    provider,
  ].find(Boolean);

  const shouldOpenInsights =
    fromFirstVideoViewEmail || fromReactionNotification;

  // the pathname gets cleared inside react after the error bar has been displayed
  if (hasParamToClear || !isEmpty(data)) {
    window.history.replaceState('', '', window.location.pathname);
  }

  if (data.referrer === 'push') {
    processPushEventData(data);
  }

  smoothscroll.polyfill();

  const ExceptionsTracker = loggerx.initTracker();
  const middleware = [thunk];

  if (ExceptionsTracker) {
    middleware.push(sentryActionState(ExceptionsTracker));
  }

  const store = storeUtil.init(reducers, middleware);
  const container = document.getElementById('container');

  handleOauthProvider(store);
  store.dispatch(updateFromPublicSharePage(true));
  store.dispatch(getUserCreator());
  store.dispatch(initVideoCreator(window[VIDEO]));
  store.dispatch(getFeatureFlagCreator(SEND_HLS_PLAYBACK_STATS));

  injectCustomBrandColors();

  if (window[VIDEO] && !window[VIDEO].current_user_is_owner) {
    const lastTheaterMode =
      parseCookie(document.cookie)[KEY_THEATER_MODE] === 'true';

    if (lastTheaterMode) {
      store.dispatch(updateTheaterMode(true));
    }
  }

  render(
    <Provider store={store}>
      <ApolloProvider client={getGraphQLClient()}>
        <UrlQueryParamContextWrapper>
          <ShareVideo
            focusTitle={focusTitleParam}
            mutedParam={mutedParam}
            timeParam={timeParam}
            shouldOpenInsights={shouldOpenInsights}
            fromRecorderParam={fromRecorderParam}
            recordingLimitParam={recordingLimitParam}
          />
          <wru.Overlay />
        </UrlQueryParamContextWrapper>
        <ModalContainer />
      </ApolloProvider>
    </Provider>,
    container
  );
}

function handleOauthProvider(store) {
  if (isGoogleApp) {
    store.dispatch(toggleGoogleInviteModal(true));
    oauthError && store.dispatch(toggleGoogleOAuthError(true));

    return;
  }

  oauthError &&
    store.dispatch(showErrorBar(ERROR, decodeURIComponent(oauthError)));
}

try {
  window[VIDEO] && init();
} catch (e) {
  // ignore unactionable errors
  if (!['SecurityError'].includes(e.name)) {
    loggerx.error(
      e,
      {
        message: 'Share Video page',
        hasVideo: Boolean(window?.[VIDEO]),
      },
      { team: 'create' }
    );
  }
}

// video does not exist anymore
if (!window[VIDEO]) {
  window.location.href = NOT_FOUND;
}
